/**
 * Client-side mirror of the server buyer-profile question config types
 * (`server/src/services/buyerProfile/questions.ts`). The server is the source
 * of truth; the client fetches the config at runtime. Keep these shapes in
 * sync if the server config shape changes.
 */

export type FieldRole = 'hard_filter' | 'weight' | 'signal' | 'routing';

export type PreferenceLevel = 'yes' | 'maybe' | 'no';
/**
 * Engine-side resolved preference. `missing` means the deal had data but the
 * buyer expressed no preference — scored 0 / possible, no exclusion hit.
 */
export type ResolvedPreference = PreferenceLevel | 'missing';

export type QuestionType = 'multiselect' | 'range' | 'text';

export type DisplayRule =
  | { always: true }
  | { showIf: { question: string; includesAny: string[] } }
  | { showIf: { question: string; selectedCountLte: number } }
  | { showIf: { question: string; selectedCountGte: number } };

export interface QuestionOption {
  value: string;
  label: string;
  /**
   * Optional grouping tag — currently the state code on each `utility_zones`
   * option. Drives the state-name sub-headings the form renders and is the key
   * `optionGroupFilter` matches against. Mirrors the server `QuestionOption`.
   */
  group?: string;
  /**
   * Self-referencing option visibility: the option renders only when one of
   * these values is marked yes/maybe on the *same* question (e.g. the landfill
   * `mounting_type` options appear only once a Ground mounting is selected).
   * Mirrors the server `QuestionOption.showIfSelected`.
   */
  showIfSelected?: string[];
}

export interface QuestionDef {
  key: string;
  order: number;
  label: string;
  type: QuestionType;
  options?: QuestionOption[];
  display: DisplayRule;
  roles: FieldRole[];
  tooltip: string;
  /** Optional sub-heading shown beneath the question label. */
  description?: string;
  /**
   * Optional callout explaining why a conditionally-shown question is visible
   * (e.g. "Shown because you selected PV..."). Mirrors the server config.
   */
  visibilityNote?: string;
  /**
   * Display-only override for the form section a question groups under (e.g.
   * `'itc'` to pull a signal-only question into the "ITC & Safe Harbor" section
   * instead of "Additional details"). Mirrors the server config.
   */
  formSection?: FormSectionKey;
  /**
   * Range questions only — the units offered. A single-element list (e.g.
   * `['MW']`) pins the unit and hides the toggle. Mirrors the server config.
   */
  rangeUnits?: ('MW' | '$')[];
  /**
   * Range questions only — when true, the preference-band gradations are hidden
   * and the buyer gives a single min/max range. Mirrors the server config.
   */
  rangeHideGradations?: boolean;
  /** Range questions only — which role each bound carries (Phase 3 scorer). */
  rangeRoles?: { min: FieldRole; max: FieldRole };
  perSegment?: boolean;
  /**
   * Visual nesting only — the form renders this question's options as
   * indented sub-rows under the named parent option inside the parent
   * question's preference grid, instead of as a top-level card.
   */
  nestUnder?: { question: string; option: string };
  /**
   * Render-only option filter. When set, the form shows only this question's
   * options whose `group` is among the values marked yes/maybe on the
   * referenced question (see `visibleOptionsForQuestion`). `utility_zones` uses
   * `{ question: 'geography' }` to show only the zones for selected states.
   * Mirrors the server `QuestionDef.optionGroupFilter`.
   */
  optionGroupFilter?: { question: string };
  /**
   * Phase 3 scoring axis (= form section). Omitted for signal-only questions
   * (`notes`, `itc_eligible`, `itc_adders`), which the form groups into a
   * trailing signal section. Mirrors the server `QuestionDef.category`.
   */
  category?: DealCategory;
  /**
   * Phase 3 scoring — the question's "total points possible" (workbook
   * sub-category weight). Omitted for signal-only questions. Mirrors the server
   * `QuestionDef.weight`.
   */
  weight?: number;
}

export interface QuestionConfig {
  version: string;
  questions: QuestionDef[];
}

/** multiselect answer payload: option value -> preference level. */
export type MultiselectAnswer = Record<string, PreferenceLevel>;

export interface RangeGradation {
  from: number;
  to: number;
  pref: PreferenceLevel;
}

export interface RangeAnswer {
  unit?: 'MW' | '$';
  min?: number | null;
  max?: number | null;
  gradations?: RangeGradation[];
}

export interface TextAnswer {
  value: string;
}

export type AnswerPayload = MultiselectAnswer | RangeAnswer | TextAnswer;

/** One answer entry — matches a buyer_profile_answer row. */
export interface BuyerProfileAnswerEntry {
  questionKey: string;
  segment?: string | null;
  answer: AnswerPayload;
  /** ISO timestamp the row was last saved. Present on entries from the server. */
  updatedAt?: string;
}

/** Phase 2 onboarding-wizard resume cursor, persisted on the buyer profile. */
export interface OnboardingState {
  /** Index into ONBOARDING_STEPS the buyer last reached. */
  stepIndex: number;
}

export interface BuyerProfile {
  id: string;
  companyId: string;
  questionConfigVersion: string;
  status: 'draft' | 'onboarded';
  onboardingState?: OnboardingState | null;
  answers: BuyerProfileAnswerEntry[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Phase 3 match-scoring result — mirrors `MatchResult` in
 * `server/src/services/buyerProfile/match.ts`. Re-edit both in lockstep
 * if either changes.
 */
export type DealCategory =
  | 'deal_type'
  | 'deal_size'
  | 'geography'
  | 'revenue_profile'
  | 'deal_stage'
  | 'seller_profile'
  | 'other';

/**
 * Canonical render order for the spider chart, category table, and
 * per-category accordion — so an investor without (say) `seller_profile` data
 * still produces a seven-axis chart of the same shape as everyone else. Mirror
 * any change here in `server/.../questions.ts` `DealCategory`.
 */
export const CATEGORY_DISPLAY_ORDER: DealCategory[] = [
  'deal_type',
  'deal_size',
  'geography',
  'revenue_profile',
  'deal_stage',
  'seller_profile',
  'other',
];

export const CATEGORY_LABELS: Record<DealCategory, string> = {
  deal_type: 'Deal Type',
  deal_size: 'Deal Size',
  geography: 'Geography',
  revenue_profile: 'Revenue Profile',
  deal_stage: 'Deal Stage',
  seller_profile: 'Seller Profile',
  other: 'Other',
};

/**
 * Shorter axis labels for the spider chart, where long names overrun the
 * canvas and overlap neighbouring axes. The full names are still used in the
 * category table and tooltips.
 */
export const CATEGORY_SHORT_LABELS: Record<DealCategory, string> = {
  deal_type: 'Deal Type',
  deal_size: 'Deal Size',
  geography: 'Geography',
  revenue_profile: 'Revenue',
  deal_stage: 'Deal Stage',
  seller_profile: 'Seller',
  other: 'Other',
};

/**
 * Buyer-profile form sections. The form groups its question cards by scoring
 * category so the input layout mirrors the match spider chart (one section per
 * axis), plus a trailing `signal` section for signal-only questions (`notes`,
 * `itc_eligible`, `itc_adders`) that carry no category. (The key is `signal`
 * rather than `other` so it doesn't collide with the `other` scoring category.)
 * Sections render in this order; an empty section (all its questions hidden by
 * display rules) is skipped. This order is independent of
 * `CATEGORY_DISPLAY_ORDER` (which drives the spider chart) — the form places
 * geography after deal stage for a more natural data-entry flow.
 */
export type FormSectionKey = DealCategory | 'itc' | 'signal';

export const FORM_SECTION_ORDER: FormSectionKey[] = [
  'deal_type',
  'deal_size',
  'revenue_profile',
  'deal_stage',
  'geography',
  'seller_profile',
  'other',
  'itc',
  'signal',
];

export const FORM_SECTION_LABELS: Record<FormSectionKey, string> = {
  ...CATEGORY_LABELS,
  itc: 'ITC & Safe Harbor',
  signal: 'Additional details',
};

export const FORM_SECTION_BLURBS: Record<FormSectionKey, string> = {
  deal_type: 'Deal structure, technology, and mounting types you cover.',
  deal_size: 'The deal and project sizes you want to see.',
  geography: 'Locations and utility zones you operate in.',
  revenue_profile: 'Market segments, revenue types, and customer credit you target.',
  deal_stage: 'Where in the development lifecycle you invest.',
  seller_profile: 'The kinds of sellers and partners you work with.',
  other: 'Other preferences.',
  itc: 'ITC eligibility, adders, and safe-harbor preferences.',
  signal: 'Deal timing and anything the questions above did not cover.',
};

export type ExclusionBand = 'green' | 'yellow' | 'red';

export interface MatchContribution {
  questionKey: string;
  category: DealCategory;
  preference: ResolvedPreference;
  /** Category weight × preference factor — points this question earned. */
  points: number;
  /** Category weight — points this question could have earned. */
  possible: number;
}

export interface MatchExclusion {
  questionKey: string;
  category: DealCategory;
  points: number;
  reason: string;
}

export interface CategoryBreakdown {
  category: DealCategory;
  possible: number;
  actual: number;
  /** round(100 × actual/possible); 0 if possible=0. */
  percent: number;
  /** Sum of exclusion points from No answers in this category. */
  exclusion: number;
  /** `(actual/possible) × ((5 − exclusion) / 5)`, clamped to [0, 1]. */
  spiderValue: number;
  questionKeys: string[];
}

export interface MatchSignal {
  questionKey: string;
  value: unknown;
}

export interface MatchResult {
  /** 0–100 weighted match score (never auto-zeroed by a No). */
  score: number;
  /** Sum of exclusionPoints across all No answers. */
  exclusionTotal: number;
  exclusionBand: ExclusionBand;
  /** True if any single applicable question contributed ≥ 5 exclusion points. */
  killed: boolean;
  categories: CategoryBreakdown[];
  contributions: MatchContribution[];
  exclusions: MatchExclusion[];
  signals: MatchSignal[];
  /** Question keys with no applicable deal data or no buyer answer. */
  skipped: string[];
}
